import React, { useState, useEffect } from 'react';
import "../../css/ExamSchedule.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaCalendarAlt, FaSearch, FaTimes } from 'react-icons/fa';
import Loader from "../../components/Loader";

function StudentExamSchedule() {
    const [exams, setExams] = useState([]);
    const [filteredExams, setFilteredExams] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchExams = async () => {
        setIsLoading(true);
        const token = localStorage.getItem("token");

        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/student/exams", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch exams");
            }

            const data = await response.json();
            setExams(data);
            setFilteredExams(data);
        } catch (error) {
            console.error("Error fetching exams:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchExams();
    }, []);

    useEffect(() => {
        let results = [...exams];

        // Apply search term filter
        if (searchTerm.trim() !== '') {
            results = results.filter(exam =>
                (exam.examName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (exam.subject?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (exam.class?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (exam.section?.toLowerCase() || '').includes(searchTerm.toLowerCase())
            );
        }

        setFilteredExams(results);
    }, [searchTerm, exams]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    const getStatusBadgeClass = (status) => {
        if (status === 'upcoming') return 'ExamSchedule-badge-upcoming';
        if (status === 'completed') return 'ExamSchedule-badge-completed';
        return '';
    };

    const getExamTypeClass = (examType) => {
        switch (examType) {
            case 'final': return 'ExamSchedule-card-final';
            case 'quarterly': return 'ExamSchedule-card-quarterly';
            case 'unit': return 'ExamSchedule-card-unit';
            default: return 'ExamSchedule-card-regular';
        }
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    return (
        <div className="ExamSchedule-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            <div className="ExamSchedule-header">
                <div className="ExamSchedule-title-container">
                    <FaCalendarAlt className="ExamSchedule-title-icon" />
                    <h1 className="ExamSchedule-title">Exam Schedule</h1>
                </div>

                <div className="ExamSchedule-stats">
                    <div className="ExamSchedule-stat-box">
                        <div className="ExamSchedule-stat-icon ExamSchedule-stat-icon-total">
                            <FaCalendarAlt />
                        </div>
                        <div className="ExamSchedule-stat-info">
                            <span className="ExamSchedule-stat-number">{exams.length}</span>
                            <span className="ExamSchedule-stat-label">Upcoming Exams</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="ExamSchedule-filters">
                <div className="ExamSchedule-search-container">
                    <FaSearch className="ExamSchedule-search-icon" />
                    <input
                        type="text"
                        className="ExamSchedule-search-input"
                        placeholder="Search exams by name, subject, class, or section..."
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                    {searchTerm && (
                        <button className="ExamSchedule-search-clear" onClick={clearSearch}>
                            <FaTimes />
                        </button>
                    )}
                </div>
            </div>

            <div className="ExamSchedule-list-container">
                {isLoading ? (
                    <>
                        <Loader />
                        <p>Loading...</p>
                    </>
                ) : filteredExams.length === 0 ? (
                    <div className="ExamSchedule-empty-state">
                        <FaCalendarAlt className="ExamSchedule-empty-icon" />
                        <h3>No Upcoming Exams</h3>
                        {searchTerm ? (
                            <p>No exams match your search. Try adjusting your search criteria.</p>
                        ) : (
                            <p>There are no upcoming exams scheduled for your class.</p>
                        )}
                        {searchTerm && (
                            <button className="ExamSchedule-btn ExamSchedule-btn-primary" onClick={clearSearch}>
                                Clear Search
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="ExamSchedule-grid">
                        {filteredExams.map((exam) => {
                            const examTypeClass = getExamTypeClass(exam.examType);
                            const statusBadgeClass = getStatusBadgeClass(exam.status);

                            return (
                                <div
                                    key={exam._id}
                                    className={`ExamSchedule-card ${examTypeClass}`}
                                >
                                    <div className="ExamSchedule-card-header">
                                        <div className={`ExamSchedule-card-badge ${statusBadgeClass}`}>
                                            {exam.status === 'upcoming' ? 'Upcoming' : 'Completed'}
                                        </div>
                                        <div className={`ExamSchedule-card-type-badge`}>
                                            {exam.examType === 'regular' ? 'Regular' :
                                                exam.examType === 'final' ? 'Final' :
                                                    exam.examType === 'quarterly' ? 'Quarterly' : 'Unit Test'}
                                        </div>
                                        <h3 className="ExamSchedule-card-title">
                                            {exam.examName}
                                        </h3>
                                    </div>
                                    <div className="ExamSchedule-card-body">
                                        <div className="ExamSchedule-card-detail-row">
                                            <div className="ExamSchedule-card-detail">
                                                <span className="ExamSchedule-card-label">Subject:</span>
                                                <span className="ExamSchedule-card-value">{exam.subject}</span>
                                            </div>
                                            <div className="ExamSchedule-card-detail">
                                                <span className="ExamSchedule-card-label">Class:</span>
                                                <span className="ExamSchedule-card-value">{exam.class} - {exam.section}</span>
                                            </div>
                                        </div>
                                        <div className="ExamSchedule-card-detail-row">
                                            <div className="ExamSchedule-card-detail">
                                                <span className="ExamSchedule-card-label">Date:</span>
                                                <span className="ExamSchedule-card-value">{formatDate(exam.date)}</span>
                                            </div>
                                            <div className="ExamSchedule-card-detail">
                                                <span className="ExamSchedule-card-label">Time:</span>
                                                <span className="ExamSchedule-card-value">{exam.startTime} - {exam.endTime}</span>
                                            </div>
                                        </div>
                                        <div className="ExamSchedule-card-detail-row">
                                            <div className="ExamSchedule-card-detail">
                                                <span className="ExamSchedule-card-label">Total Marks:</span>
                                                <span className="ExamSchedule-card-value">{exam.totalMarks}</span>
                                            </div>
                                            <div className="ExamSchedule-card-detail">
                                                <span className="ExamSchedule-card-label">Passing Marks:</span>
                                                <span className="ExamSchedule-card-value">{exam.passingMarks}</span>
                                            </div>
                                        </div>
                                        <div className="ExamSchedule-card-description">
                                            <span className="ExamSchedule-card-label">Description:</span>
                                            <p className="ExamSchedule-card-description-text">{exam.description}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

export default StudentExamSchedule;